import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const DocumentViewer = ({ isOpen, onClose, document }) => {
  if (!isOpen || !document) return null;

  const getDocumentTypeIcon = () => {
    if (document?.type === 'image') {
      return 'Image';
    }
    return 'FileText';
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name={getDocumentTypeIcon()} size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{document?.name}</h2>
              <p className="text-sm text-muted-foreground">
                {document?.size} • Uploaded {document?.uploadDate}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              document?.status === 'completed' 
                ? 'bg-success/10 text-success' 
                : document?.status === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
            }`}>
              <Icon 
                name={
                  document?.status === 'completed' 
                    ? 'CheckCircle' 
                    : document?.status === 'pending' ?'Clock' :'XCircle'
                } 
                size={14} 
              />
              <span className="text-sm font-medium capitalize">{document?.status}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            />
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {document?.type === 'image' ? (
            <div className="flex justify-center">
              <div className="max-w-full max-h-[600px] overflow-hidden rounded-lg border border-border">
                <Image
                  src={document?.url}
                  alt={`${document?.documentType} document preview showing uploaded verification document`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Icon name="FileText" size={64} className="text-muted-foreground" />
              <div className="text-center">
                <h3 className="text-lg font-medium text-foreground">PDF Document</h3>
                <p className="text-sm text-muted-foreground">
                  Click download to view the full document
                </p>
              </div>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
              >
                Download PDF
              </Button>
            </div>
          )}

          {document?.status === 'rejected' && document?.rejectionReason && (
            <div className="mt-6 p-4 bg-error/10 rounded-lg border border-error/20">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-error">Document Rejected</h4>
                  <p className="text-sm text-error/80 mt-1">{document?.rejectionReason}</p>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Upload New Document
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {document?.status === 'completed' && (
            <div className="mt-6 p-4 bg-success/10 rounded-lg border border-success/20">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <div>
                  <h4 className="text-sm font-medium text-success">Document Verified</h4>
                  <p className="text-sm text-success/80">
                    This document has been successfully verified and approved
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;